import { useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { generatePath, useNavigate, useOutletContext, useParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';

import {
  PermissionKeys,
  TenantMemberReadModel,
  useDeleteCompaniesByCompanyIdMembersInviteAndInvitationIdMutation,
  useDeleteCompaniesByCompanyIdPersonsAndUserIdMutation,
  useGetCompaniesByCompanyIdMembersQuery,
  usePostCompaniesByCompanyIdPersonsAndUserIdReactivateMutation,
  usePostCompaniesByCompanyIdMembersInviteAndInvitationIdResendMutation,
} from '@client/api';
import {
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  CardTitle,
  EmptyPlaceholder,
  isDefinedNumber,
  Modal,
  ModalContent,
  ModalFooter,
  Pagination,
  Table,
  TableBody,
  TableHead,
  TableHeadCell,
  TableHeadRow,
  Text,
  Typography,
} from '@client/toolkit';

import { CompanyUserRow } from './company-user-row.component';
import { CompanyUserInviteModal } from './company-user-invite-modal.component';
import { CompaniesPaths } from '../../../companies.router';
import { PermissionBasedActionButton } from '../../../../../components/permission-based-button/permission-based-button';
import { useSession } from '../../../../../hooks/useSession/useSession';

export function CompanyUsersCard() {
  const { t: tCommon } = useTranslation();
  const { t: tGlobal } = useTranslation('global');
  const navigate = useNavigate();

  const { id } = useParams<{ id: string }>();
  const { sessionUser } = useSession();
  const currentUserId = sessionUser?.id;
  const { refetchMembersCount } = useOutletContext<{ refetchMembersCount: () => void }>();

  const pageSize = 100;
  const [page, setPage] = useState(0);
  const [inviting, setInviting] = useState(false);
  const [deletingUser, setDeletingUser] = useState<TenantMemberReadModel | null>(null);

  const { data, isLoading, isFetching, refetch } = useGetCompaniesByCompanyIdMembersQuery(
    {
      companyId: id ?? '',
      'pagination.Limit': pageSize,
      'pagination.Offset': page * pageSize,
      includeInactive: true,
    },
    { skip: id == null },
  );

  const [deleteInvitation, { isLoading: isDeletingInvitation }] =
    useDeleteCompaniesByCompanyIdMembersInviteAndInvitationIdMutation();
  const [deleteUser, { isLoading: isDeletingUser }] = useDeleteCompaniesByCompanyIdPersonsAndUserIdMutation();
  const [reactivateUser, { isLoading: isReactivating }] =
    usePostCompaniesByCompanyIdPersonsAndUserIdReactivateMutation();
  const [resendInvitation, { isLoading: isResending }] =
    usePostCompaniesByCompanyIdMembersInviteAndInvitationIdResendMutation();

  const isDeleting = isDeletingInvitation || isDeletingUser;
  const isChangingStatus = isReactivating || isDeletingUser || isResending;

  const users = useMemo(() => data?.items ?? [], [data?.items]);
  const hasItems = Array.isArray(users) && users.length > 0;
  const totalCount = data?.totalCount ?? 0;

  const handleOpenDelete = (user: TenantMemberReadModel) => {
    setDeletingUser(user);
  };

  const handleCloseDelete = () => {
    setDeletingUser(null);
  };

  const handleDelete = async () => {
    if (!deletingUser?.id) return;

    try {
      if (deletingUser.status === 'Invited') {
        await deleteInvitation({ companyId: id!, invitationId: deletingUser.id }).unwrap();
      } else if (deletingUser.userId) {
        await deleteUser({ companyId: id!, userId: deletingUser.userId }).unwrap();
      } else {
        toast.error(tCommon('errorMissingUserId'));
        return;
      }
      setDeletingUser(null);
      refetch();
      refetchMembersCount();
    } catch (e) {
      // Error handling
    }
  };

  const handleResendInvite = async (user: TenantMemberReadModel) => {
    if (!user.id) {
      toast.error(tCommon('errorMissingInvitationId'));
      return;
    }

    try {
      await resendInvitation({ companyId: id!, invitationId: user.id }).unwrap();
      toast.success(tCommon('invitationResent'));
      refetch();
    } catch (e) {
      // Error handling
    }
  };

  const handleStatusChange = async (user: TenantMemberReadModel, newStatus: 'Active' | 'Inactive') => {
    if (!user.userId) {
      toast.error(tCommon('errorMissingUserId'));
      return;
    }

    if (user.status === newStatus) return;

    try {
      if (newStatus === 'Inactive') {
        await deleteUser({ companyId: id!, userId: user.userId }).unwrap();
        toast.success(tCommon('userDeactivated'));
      } else {
        await reactivateUser({ companyId: id!, userId: user.userId }).unwrap();
        toast.success(tCommon('userReactivated'));
      }
      refetch();
      refetchMembersCount();
    } catch (e) {
      // Error handling
    }
  };

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="flex-1" variant="dense">
            {tGlobal('entity.user', { context: 'plural' })}
            <Text
              as="span"
              className="inline-block align-top ml-1 font-light"
              loading={isFetching}
              isEmpty={!isDefinedNumber(totalCount)}
              emptyValue={0}
              SkeletonProps={{ className: 'min-w-[20px]' }}
            >
              ({totalCount})
            </Text>
          </CardTitle>
          <CardActions>
            <PermissionBasedActionButton
              color="primary-dark"
              size="sm"
              onClick={() => setInviting(true)}
              permissions={[PermissionKeys.Tenant.CompanyUserInvite]}
              disabled={isFetching || id == null}
              title={tCommon('invite')}
            />
          </CardActions>
        </CardHeader>

        <CardContent variant="fill" className="rounded-lg w-full px-4 pb-4">
          <Table className="overflow-hidden w-full mb-4 rounded-md" size="xs" layout="fixed">
            {!isLoading && !hasItems && (
              <caption className="px-4 py-4 [caption-side:bottom]">
                <EmptyPlaceholder>
                  {tCommon('noEntityFound', { entity: tGlobal('entity.user', { context: 'plural' }) })}
                </EmptyPlaceholder>
              </caption>
            )}

            <TableHead>
              <TableHeadRow>
                <TableHeadCell>{tGlobal('user.name')}</TableHeadCell>
                <TableHeadCell>{tGlobal('user.email')}</TableHeadCell>
                <TableHeadCell>{tGlobal('user.language')}</TableHeadCell>
                <TableHeadCell>{tGlobal('user.timezone')}</TableHeadCell>
                <TableHeadCell>{tGlobal('user.role')}</TableHeadCell>
                <TableHeadCell>{tGlobal('user.status')}</TableHeadCell>
                <TableHeadCell className="w-[1%]" align="right">
                  <span className="sr-only">Actions</span>
                </TableHeadCell>
              </TableHeadRow>
            </TableHead>
            <TableBody>
              {isLoading && Array.from(Array(5).keys()).map((x) => <CompanyUserRow key={x} isFetching />)}

              {!isLoading &&
                hasItems &&
                users.map((user) => (
                  <CompanyUserRow
                    key={user.id}
                    user={user}
                    isFetching={isFetching || isChangingStatus}
                    isCurrentUser={user.userId === currentUserId}
                    onClickDelete={handleOpenDelete}
                    onStatusChange={handleStatusChange}
                    onResendInvite={handleResendInvite}
                  />
                ))}
            </TableBody>
          </Table>

          <Pagination totalCount={totalCount} currentPage={page} setPage={setPage} pageSize={pageSize} />
        </CardContent>
      </Card>

      {/* Delete Confirmation Modal */}
      {deletingUser && (
        <Modal open={!!deletingUser} onClose={handleCloseDelete} closeDisabled={isDeleting}>
          <ModalContent>
            <div className="uppercase text-xs font-semibold p-4 text-center">{tCommon('removeConfirmation')}</div>
            <Typography align="center" className="px-4 py-4">
              <Trans
                t={tCommon}
                i18nKey={'removeConfirm'}
                values={{ name: deletingUser?.name ?? deletingUser?.email }}
                components={{ bold: <strong /> }}
              />
            </Typography>
          </ModalContent>
          <ModalFooter className="justify-center">
            <Button color="danger" onClick={handleDelete} progressing={isDeleting}>
              {tCommon('remove')}
            </Button>
            <Button color="primary-dark" onClick={handleCloseDelete}>
              {tCommon('cancel')}
            </Button>
          </ModalFooter>
        </Modal>
      )}

      {inviting && (
        <CompanyUserInviteModal
          companyId={id}
          open={inviting}
          onClose={() => setInviting(false)}
          onSubmited={() => {
            setInviting(false);
            refetch();
            refetchMembersCount();
          }}
        />
      )}
    </>
  );
}
